import { PageTitle } from "../../components";
import Menu from "../../components/VerticalMenu";
import { useEffect, useState } from "react";
import { Card, Col, Row, Statistic, Table, Tag, Spin } from "antd";
import {
  UserOutlined,
  TeamOutlined,
  TrophyOutlined,
  CalendarOutlined,
  RiseOutlined,
  FallOutlined,
} from "@ant-design/icons";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/lib/table";

interface DashboardStats {
  totalArchers: number;
  totalTeams: number;
  totalReferees: number;
  totalLeaders: number;
  activeCompetitions: number;
  completedCompetitions: number;
  totalRanks: number;
}

function Home() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalArchers: 0,
    totalTeams: 0,
    totalReferees: 0,
    totalLeaders: 0,
    activeCompetitions: 0,
    completedCompetitions: 0,
    totalRanks: 0,
  });
  const [recentCompetitions, setRecentCompetitions] = useState<any[]>([]);
  const [latestNews, setLatestNews] = useState<any[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch all data in parallel for better performance
        const [
          archersSnapshot,
          teamsSnapshot,
          refereesSnapshot,
          leadersSnapshot,
          activeCompetitionsSnapshot,
          completedCompetitionsSnapshot,
          recentCompetitionsSnapshot,
          ranksSnapshot,
          newsSnapshot,
        ] = await Promise.all([
          // Archers
          getDocs(
            query(collection(db, "users"), where("role", "==", "archer"))
          ),
          // Teams
          getDocs(
            query(collection(db, "teams"), where("clubId", "==", CURRENT_CLUB_ID))
          ),
          // Referees
          getDocs(
            query(
              collection(db, "users"),
              where("clubId", "==", CURRENT_CLUB_ID),
              where("role", "==", "referee")
            )
          ),
          // Leaders
          getDocs(
            query(
              collection(db, "adminUsers"),
              where("clubId", "==", CURRENT_CLUB_ID),
              where("role", "==", "leader")
            )
          ),
          // Active Competitions
          getDocs(
            query(
              collection(db, "competitions"),
              where("clubId", "==", CURRENT_CLUB_ID),
              where("status", "==", "open")
            )
          ),
          // Completed Competitions
          getDocs(
            query(
              collection(db, "competitions"),
              where("clubId", "==", CURRENT_CLUB_ID),
              where("status", "==", "finished")
            )
          ),
          // Recent Competitions
          getDocs(
            query(
              collection(db, "competitions"),
              where("clubId", "==", CURRENT_CLUB_ID)
            )
          ),
          // Ranks
          getDocs(
            query(collection(db, "ranks"), where("clubId", "==", CURRENT_CLUB_ID))
          ),
          // Latest News
          getDocs(
            query(collection(db, "news"), where("clubId", "==", CURRENT_CLUB_ID))
          ),
        ]);

        // Process latest news
        const newsData: any[] = [];
        newsSnapshot.forEach((doc) => {
          const data = doc.data();
          newsData.push({
            id: doc.id,
            title: data.title,
            description: data.description,
            createdAt: data.createdAt,
          });
        });
        newsData.sort((a, b) => {
          const dateA = a.createdAt?.seconds || 0;
          const dateB = b.createdAt?.seconds || 0;
          return dateB - dateA;
        });
        setLatestNews(newsData.slice(0, 5));

        // Process recent competitions
        const recentComps: any[] = [];
        recentCompetitionsSnapshot.forEach((doc) => {
          const data = doc.data();
          recentComps.push({
            id: doc.id,
            name: data.name,
            startDate: data.startDate,
            status: data.status,
            location: data.location,
          });
        });

        // Sort by start date and get recent 5
        recentComps.sort((a, b) => {
          const dateA = a.startDate?.seconds || 0;
          const dateB = b.startDate?.seconds || 0;
          return dateB - dateA;
        });
        setRecentCompetitions(recentComps.slice(0, 5));

        // Set all stats
        setStats({
          totalArchers: archersSnapshot.size,
          totalTeams: teamsSnapshot.size,
          totalReferees: refereesSnapshot.size,
          totalLeaders: leadersSnapshot.size,
          activeCompetitions: activeCompetitionsSnapshot.size,
          completedCompetitions: completedCompetitionsSnapshot.size,
          totalRanks: ranksSnapshot.size,
        });

        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const competitionColumns: ColumnsType<any> = [
    {
      title: "Тэмцээний нэр",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Байршил",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Огноо",
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => {
        if (date?.seconds) {
          return dayjs(date.seconds * 1000).format("YYYY-MM-DD");
        }
        return "-";
      },
    },
    {
      title: "Төлөв",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "open" ? "green" : "blue"}>
          {status === "open" ? "Нээлттэй" : "Дууссан"}
        </Tag>
      ),
    },
  ];

  const newsColumns: ColumnsType<any> = [
    {
      title: "№",
      key: "index",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Гарчиг",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Тайлбар",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => {
        if (date?.seconds) {
          return dayjs(date.seconds * 1000).format("YYYY-MM-DD");
        }
        return "-";
      },
    },
  ];

  return (
    <>
      <PageTitle title="Тавтай морилно уу" description="Нүүр хуудас" />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          {loading ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Нийт харваачид"
                      value={stats.totalArchers}
                      prefix={<UserOutlined />}
                      valueStyle={{ color: "#3f8600" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Нийт багууд"
                      value={stats.totalTeams}
                      prefix={<TeamOutlined />}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Удахгүй болох тэмцээн"
                      value={stats.activeCompetitions}
                      prefix={<TrophyOutlined />}
                      valueStyle={{ color: "#cf1322" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Дууссан тэмцээн"
                      value={stats.completedCompetitions}
                      prefix={<CalendarOutlined />}
                      valueStyle={{ color: "#722ed1" }}
                    />
                  </Card>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Нийт шүүгчид"
                      value={stats.totalReferees}
                      prefix={<UserOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Багийн ахлагчид"
                      value={stats.totalLeaders}
                      prefix={<TeamOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Нийт цол"
                      value={stats.totalRanks}
                      prefix={<RiseOutlined />}
                    />
                  </Card>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} lg={12}>
                  <Card title="Сүүлийн тэмцээнүүд" bordered={false}>
                    <Table
                      columns={competitionColumns}
                      dataSource={recentCompetitions}
                      pagination={false}
                      rowKey="id"
                      size="small"
                    />
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="Сүүлийн мэдээ" bordered={false}>
                    <Table
                      columns={newsColumns}
                      dataSource={latestNews}
                      pagination={false}
                      rowKey="id"
                      size="small"
                    />
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>
      </div>
    </>
  );
}
export default Home;
